/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import styles from './AddressUpdateModal.module.css';
import { addInfluencerAddress, getLocationDetails, updateInfluencerAddress } from '../../../services/dashboard.service';
import { FormGroup } from '../../common';
import { Button, Heading } from '../../styledComponents';

type AddressUpdateModalProps = {
  address: any;
  setShowUpdateAddressModal: React.Dispatch<React.SetStateAction<boolean>>;
  fetchHomePageData: () => void;
  profileId: string;
  withoutModal?: boolean;
  modalTitle?: React.ReactNode;
  onSaveComplete?: () => unknown;
  actionButtonText?: string;
};

type AddressDetailsType = {
  fullName: string;
  addressLineOne: string;
  addressLineTwo: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
};

type AddressBodyType = {
  fullName: string;
  addressLineOne: string;
  addressLineTwo: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
  addressId?: string;
  influencerId?: string;
};

export default function AddressUpdateModal({
  address,
  setShowUpdateAddressModal,
  fetchHomePageData,
  profileId,
  withoutModal = false,
  modalTitle,
  onSaveComplete,
  actionButtonText = 'Save',
}: AddressUpdateModalProps) {
  const [addressDetails, setAddressDetails] = useState<AddressDetailsType>({
    fullName: address?.fullName,
    addressLineOne: address?.addressLineOne,
    addressLineTwo: address?.addressLineTwo,
    pincode: address?.pincode,
    city: address?.city,
    state: address?.state,
    country: address?.country,
  });

  const modalRef = useRef<null | HTMLDivElement>(null);

  const handleOutsideClick = (e: any) => {
    if (!modalRef.current?.contains(e.target)) {
      setShowUpdateAddressModal(false);
    }
  };

  const checkIfAddressPresent = () => {
    const { addressLineOne, pincode, city, country } = addressDetails;
    if (!addressLineOne || !pincode || !city || !country) return false;
    return true;
  };

  const handleUpdateAddress = async () => {
    const body: AddressBodyType = {
      fullName: addressDetails?.fullName || '',
      addressLineOne: addressDetails?.addressLineOne || '',
      addressLineTwo: addressDetails?.addressLineTwo || '',
      pincode: addressDetails?.pincode || '',
      city: addressDetails?.city || '',
      state: addressDetails?.state || '',
      country: addressDetails?.country || '',
    };

    if (!address) {
      body.influencerId = profileId;
      const response = await addInfluencerAddress(body);
      if (response.status) {
        toast.success(response.data.message);
        setShowUpdateAddressModal(false);
        fetchHomePageData();
        onSaveComplete?.();
      }
    } else {
      body.addressId = address._id;
      const response = await updateInfluencerAddress(body);
      if (response.status) {
        toast.success(response.data.message);
        setShowUpdateAddressModal(false);
        fetchHomePageData();
        onSaveComplete?.();
      }
    }
  };

  const handleFetchLocation = async (zipcode: string) => {
    const data = await getLocationDetails(zipcode);
    setAddressDetails((prev) => ({ ...prev, ...data }));
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'pincode' && value !== '') {
      if (value.length <= 8) {
        setAddressDetails((prev) => ({ ...prev, [name]: value }));
        if (value.length > 3) {
          handleFetchLocation(value);
        }
      }
    } else {
      setAddressDetails({ ...addressDetails, [name]: value });
    }
  };

  const renderModalTitle = () => {
    if (modalTitle) return modalTitle;

    return <>{!address ? 'Add' : 'Edit'} Address</>;
  };

  const renderContent = () => {
    return (
      <div className={styles.formSection}>
        <Heading as="h3" fontWeight="500" fontSize="16px" color="#353535" textTransform="uppercase">
          {renderModalTitle()}
        </Heading>
        <FormGroup
          id="fullName"
          name="fullName"
          label="Full Name"
          type="text"
          placeholder="Your Full Name"
          value={addressDetails.fullName}
          onChange={handleInputChange}
          errorText=""
          required
        />
        <FormGroup
          id="addressLineOne"
          name="addressLineOne"
          label="Address"
          type="text"
          placeholder="e.g. Empire State Building"
          value={addressDetails.addressLineOne}
          onChange={handleInputChange}
          errorText=""
          required
          autoFocus
        />
        <div className={styles.group}>
          <FormGroup
            id="addressLineTwo"
            name="addressLineTwo"
            label="Apartment, suite, etc"
            type="text"
            placeholder="e.g. 10th floor"
            value={addressDetails.addressLineTwo}
            onChange={handleInputChange}
            errorText=""
            required
          />
          <FormGroup
            id="pincode"
            name="pincode"
            label="Postal Code"
            type="text"
            placeholder="e.g. 10001"
            value={addressDetails.pincode}
            onChange={handleInputChange}
            errorText=""
            required
          />
        </div>
        <FormGroup
          id="city"
          name="city"
          label="City"
          type="text"
          placeholder="e.g. New York"
          value={addressDetails.city}
          onChange={handleInputChange}
          errorText=""
          required
        />
        <FormGroup
          id="state"
          name="state"
          label="State"
          type="text"
          placeholder="e.g. New York"
          value={addressDetails.state}
          onChange={handleInputChange}
          errorText=""
        />
        <FormGroup
          id="country"
          name="country"
          label="Country"
          type="text"
          placeholder="e.g. United States of America"
          value={addressDetails.country}
          onChange={handleInputChange}
          errorText=""
          required
        />
        <div className={styles.group}>
          <Button
            bgColor="transparent"
            color="var(--dark-grey)"
            style={{ border: '1px solid var(--light-grey)' }}
            type="button"
            onClick={() => setShowUpdateAddressModal(false)}
          >
            Cancel
          </Button>
          <Button
            bgColor="var(--saral-purple)"
            type="button"
            onClick={handleUpdateAddress}
            disabled={!checkIfAddressPresent()}
          >
            {actionButtonText}
          </Button>
        </div>
      </div>
    );
  };

  if (withoutModal) {
    return renderContent();
  }

  return (
    <div className={styles.modalWrapper} onClick={handleOutsideClick}>
      <div className={styles.modal} ref={modalRef}>
        {renderContent()}
      </div>
    </div>
  );
}
